import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * Defines the available variants for typography.
 */
export type TypographyVariant =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'button1'
  | 'button2'
  | 'body1'
  | 'body2'
  | 'label1'
  | 'label2';

/**
 * Defines the display options for typography.
 */
export type DisplayType = 'block' | 'inline' | 'inline-block';

/**
 * Defines the available color options for typography styling.
 */
export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'light'
  | 'dark'
  | 'inactive'
  | 'destructive'
  | 'highlight'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'body'
  | 'inherit';
/**
 * Defines the text alignment options for typography.
 */
export type Alignment = 'left' | 'center' | 'right' | 'justify' | 'inherit';

@Component({
  selector: 'hyperkit-typography',
  standalone: true,
  templateUrl: './typography.component.html',
  styleUrls: ['./typography.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypographyComponent {
  /**
   * The variant for the typography styling.
   * Example: 'h1', 'body1', 'label1', etc.
   */
  @Input() variant: TypographyVariant = 'body1';

  /**
   * The color variant for the typography styling.
   * Example: 'primary', 'secondary', etc.
   */
  @Input() color: TypographyColor = 'primary';

  /**
   * The text alignment for the typography.
   * Example: 'left', 'center', 'right', etc.
   */
  @Input() align: Alignment = 'left';

  /**
   * Whether the text should wrap.
   * If false, text will not wrap, and overflow will be handled with ellipsis.
   */
  @Input() wrap: boolean = true;

  /* The display type for the typography element.
  * Controls how the element is rendered in the document flow.
  */
 @Input() display: DisplayType = 'block';
 

  /**
   * Additional CSS classes to apply to the typography element.
   */
  @Input() additionalClasses: string[] = [];

  /**
   * Dynamic attributes for the element, such as `aria-label` or `data-*` attributes.
   */
  @Input() attributes: Record<string, string | number | boolean> = {};

  /**
   * Computes the full list of classes to apply to the typography element.
   * Combines the color, alignment, wrap, and additional classes.
   *
   * @returns A string of space-separated class names.
   */
  get classes(): string {
    const variantClass = this.variant && `variant-${this.variant}`; // Class for text variant
    const wrapClass = this.wrap ? 'wrap' : 'no-wrap'; // Class for text wrapping behavior
    const colorClass = this.color && `color-${this.color}`; // Class for color variant
    const alignmentClass = this.align && `align-${this.align}`; // Class for text alignment
    const displayClass = this.display && `display-${this.display}`;


    return [
      variantClass, // Variant-based class
      colorClass, // Color-based class
      alignmentClass, // Alignment-based class
      wrapClass, // Wrap behavior
      displayClass,
      ...this.additionalClasses, // Any additional custom classes
    ]
      .filter(Boolean) // Remove undefined or empty values
      .join(' '); // Combine classes with spaces
  }
}
